import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { HeroChatBubble } from './HeroChatBubble';
import { ReplyChip } from './ReplyChip';
import { ThinkingDots } from './ThinkingDots';
import type { BubbleLink, ChatMessage, ChatOption } from '../types/chat';
interface HeroChatProps {
  show: boolean;
  reduce: boolean;
  bubbleLink: BubbleLink;
  messages: ChatMessage[];
  burst: number;
  thinking: boolean;
  options: ChatOption[];
  ready: boolean;
  picked: string | null;
  onChoose: (option: ChatOption) => void;
  onClose: () => void;
}

/** Notes on screen at once (newest + 3 stacked above) */
const MAX_STACK = 4;

/**
 * Pixel's hero chat. Rendered inside the hero, pinned at Pixel's upper-right (he holds his
 * home spot while chatting). The newest note sits at the main spot with the gradient outline;
 * older notes are pushed up, fade and pop away. Thinking squares sit under it, then blue
 * reply options — early if you hover near Pixel or his notes.
 */
export function HeroChat({
  show,
  reduce,
  bubbleLink,
  messages,
  burst,
  thinking,
  options,
  ready,
  picked,
  onChoose,
  onClose
}: HeroChatProps) {
  const [near, setNear] = useState(false);
  const [mainW, setMainW] = useState(0);
  const observer = useRef<ResizeObserver | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);

  // "Near Pixel or his notes" — a generous zone around the anchor (Pixel sits below-left of it)
  useEffect(() => {
    if (!show) {
      setNear(false);
      return;
    }
    const onMove = (e: PointerEvent) => {
      const el = anchorRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const inside = e.clientX > r.left - 440 && e.clientX < r.left + Math.max(mainW, 260) + 80 && e.clientY > r.top - 300 && e.clientY < r.top + 360;
      setNear(n => n === inside ? n : inside);
    };
    window.addEventListener('pointermove', onMove, {
      passive: true
    });
    return () => window.removeEventListener('pointermove', onMove);
  }, [show, mainW]);

  // Measure the main note so the replies line up with its right edge
  const mainRef = useCallback((el: HTMLDivElement | null) => {
    observer.current?.disconnect();
    if (!el) return;
    observer.current = new ResizeObserver(([entry]) => setMainW(entry.borderBoxSize?.[0]?.inlineSize ?? el.offsetWidth));
    observer.current.observe(el);
  }, []);
  useEffect(() => () => observer.current?.disconnect(), []);

  // Room to the right of the anchor: notes wrap onto more lines rather than running off screen
  // (leaves space for the replies, which reach ~56px past the note's right edge)
  const [room, setRoom] = useState(480);
  const [narrow, setNarrow] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setNarrow(mq.matches);
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);
  useEffect(() => {
    if (!show) return;
    const measure = () => {
      const el = anchorRef.current;
      if (!el) return;
      const left = el.getBoundingClientRect().left;
      // Desktop leaves ~56px for the replies' overhang; phones don't overhang
      const overhang = window.innerWidth < 768 ? 0 : 56;
      const next = Math.max(180, Math.floor((window.innerWidth - left - 12 - overhang - 16) / 20) * 20);
      setRoom(cur => cur === next ? cur : next);
    };
    // Measured once per conversation and on resize only (rounded), never while Pixel floats —
    // a changing limit made the notes re-wrap and jitter as the stack animated
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [show]);
  const burstNotes = messages.filter(m => m.from === 'bot' && m.burst === burst).slice(-MAX_STACK);
  const main = burstNotes[burstNotes.length - 1];
  // Max 4 on screen (newest + 3 above), kept until a 5th pushes the oldest out
  const older = burstNotes.slice(0, -1);

  // Replies only after Pixel has finished his whole sequence
  const optionsOpen = !picked && options.length > 0 && ready && !thinking;
  const shownReplies: ChatOption[] = picked ? [{
    label: picked,
    next: '',
    reaction: 'neutral'
  }] : optionsOpen ? options : [];
  const hold = () => {
    bubbleLink.hovered = true;
  };
  const release = () => {
    bubbleLink.hovered = false;
  };
  return (
    // Anchor point = where the bubble's tail meets Pixel's upper-right (Pixel scales with the hero's 80vh)
    <motion.div ref={anchorRef} className="pointer-events-none absolute z-[45] h-0 w-0" style={{
      // Spots can override the anchor (e.g. phones, where Pixel sits off to the left)
      left: 'var(--chat-left, min(calc(50% + 17vh), calc(100% - 320px)))',
      top: 'var(--chat-top, calc(50% - 8vh))',
      // Floats along with Pixel's hovering / drifting (lagged by the scene)
      x: bubbleLink.dx,
      y: bubbleLink.dy
    }} data-id="element-81" data-mp="30h9hggtbym28">
      <AnimatePresence data-id="element-82" data-mp="dm1njyva6wsf">
        {show && <motion.div key="hero-chat" className="absolute left-0 top-0" initial={{
          opacity: 0,
          scale: 0.92
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.9,
          transition: {
            duration: 0.16,
            ease: [0.23, 1, 0.32, 1]
          }
        }} transition={reduce ? {
          duration: 0
        } : {
          type: 'spring',
          stiffness: 480,
          damping: 30,
          mass: 0.7
        }} style={{
          originX: 0,
          originY: 0
        }} data-id="element-83" data-mp="dk8k0sfysson">
            {/* Notes — grow upward from the main spot */}
            <div role="log" aria-live="polite" aria-label="Pixel says"
          // Bubble sits so its tail's point lands on the anchor (tail reaches ~0.4em left of the bubble)
          className="pointer-events-auto absolute bottom-[4px] left-[12px] flex w-max flex-col items-start gap-[6px] text-left" style={{
            ['--chat-room' as string]: `${room}px`
          }} onPointerEnter={hold} onPointerLeave={release} data-id="element-84" data-mp="1191j2xrvvgnh">
              <AnimatePresence initial={false} mode="popLayout" data-id="element-85" data-mp="f7d7lemcjrpe">
                {older.map(m => <HeroChatBubble key={m.id} text={m.text} main={false} reduce={reduce} data-id="element-86" data-mp="t74nxm0c5k6k" />)}
                {main && <HeroChatBubble key={main.id} ref={mainRef} text={main.text} main reduce={reduce} data-id="element-87" data-mp="o70bq86gnbm5" />}
              </AnimatePresence>
            </div>

            {/* Under the main note: thinking bubble, then replies aligned to its right edge */}
            <div className="absolute left-[12px] top-[10px] flex flex-col"
          // Replies always end flush with the last note's right edge, directly beneath it.
          // mainW keeps the last measured width, so they stay put while notes pop away.
          // Phones: no overhang — replies end exactly at the last note's right edge, and never past the room
          // available on screen
          style={{
            width: narrow ? Math.min(mainW || 200, room) : (mainW || 260) + 56
          }} onPointerEnter={hold} onPointerLeave={release} data-id="element-88" data-mp="1bxcphzoe02kn">
              <div className="absolute left-0 top-0 pl-[15px] pt-[6px]" data-id="element-89" data-mp="17tbj03xructy">
                <AnimatePresence data-id="element-90" data-mp="1ckjt726xe286">{thinking && <ThinkingDots key="think" reduce={reduce} data-id="element-91" data-mp="1cktliwfg0lh1" />}</AnimatePresence>
              </div>
              <div role="group" aria-label="Reply to Pixel" className="flex flex-col items-end gap-[5px]" data-id="element-92" data-mp="xwf8jsobbgmr">
                <AnimatePresence data-id="element-93" data-mp="12tyvdgz2t1qb">
                  {shownReplies.map((option, i) => <ReplyChip key={option.label} option={option} index={i} count={shownReplies.length} picked={!!picked} disabled={!!picked} reduce={reduce} onChoose={onChoose} data-id="element-94" data-mp="ouphwp298lq1" data-mpi={i} />)}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </motion.div>
  );
}